import React, { useMemo } from 'react';
import { useEffect } from 'react';
import Router, { useRouter } from 'next/router';
import { FullScreenLoading } from '@components/basics/FullScreenLoading';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';

export type ProtectedRoutes = {
  path: Pathname;
  setting?: 'onlyArtist' | 'onlyNonArtist' | 'soundTeam';
}[];

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2508/add-tests-for-privaterouteguard
/**
 * Wrap in `_app` and this will redirect to `/` if user is not logged in.
 * You just pass the base path of the route you want to protect and all sub-paths will be protected.
 *
 * @example This will protect all routes starting with `/admin`
 * ```js
 *  <PrivateRouteGuard routes={[{path:"admin"}]}>
 *    <ProtectUs />
 *  </PrivateRouteGuard>
 * ```
 */
export default function PrivateRouteGuard({
  children,
  routes,
}: {
  children: JSX.Element;
  routes: ProtectedRoutes;
}) {
  const { pathname } = useRouter();
  const { authUser, isConnectingAPIUser, isAuthenticatedWithAPI, isHydrated } = useAuth();

  const pathProtection = useMemo(() => {
    // match exact paths if not fallback to any route for that path
    return routes.find(pathSetting => pathname === pathSetting.path);
  }, [pathname, routes]);

  const soundTeam = !!authUser?.roles?.isAdmin || !!authUser?.roles?.isArtistRelations;

  const isArtist = !!authUser?.artist;

  const state = useMemo((): 'redirect' | 'continue' | 'loading' => {
    // No protection, move on
    if (!pathProtection) return 'continue';

    // API is connecting, wait
    if (isConnectingAPIUser || !isHydrated) return 'loading';

    // If user is not authenticated and route is protected, redirect to home
    if (!isAuthenticatedWithAPI) return 'redirect';

    switch (pathProtection.setting) {
      case 'soundTeam': {
        if (soundTeam) return 'continue';

        return 'redirect';
      }
      case 'onlyArtist': {
        if (isArtist) return 'continue';

        // If user is not an artist and route is artist only, redirect to home
        return 'redirect';
      }
      case 'onlyNonArtist': {
        if (!isArtist) return 'continue';

        // If user is an artist and route is only for non artist users, redirect to home
        return 'redirect';
      }
      default: {
        return isAuthenticatedWithAPI ? 'continue' : 'redirect';
      }
    }
  }, [
    isConnectingAPIUser,
    pathProtection,
    isAuthenticatedWithAPI,
    isArtist,
    soundTeam,
    isHydrated,
  ]);

  useEffect(() => {
    if (state === 'redirect') Router.push('/');
  }, [state]);

  if (state !== 'continue') {
    return <FullScreenLoading />;
  }

  return children;
}
